import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

/**
 * Custom React hook to check if Integration MFE is enabled.
 * 
 * @returns {Object} Hook state object
 * @returns {boolean} returns.isMfeEnabled - Whether Integration MFE is enabled
 * @returns {boolean} returns.isLoading - Whether the feature flag check is in progress
 * @returns {boolean} returns.isError - Whether there was an error checking the feature flag
 * @returns {Error|null} returns.error - The error object if feature flag check failed
 */
const useIntegrationFeatureFlag = () => {
  const { INTEGRATION_SERVICE_ENABLED } = useSelector(state => state.user?.domainInfo || {});

  const { data: isMfeEnabled = false, isLoading, error, isError } = useQuery({
    queryKey: ['integration-feature-flag'],
    queryFn: () => {
      return new Promise((resolve, reject) => {
        if (window.envConfig?.INTEGRATION_SERVICE_ENABLED !== undefined) {
          resolve(window.envConfig.INTEGRATION_SERVICE_ENABLED === true);
        } else {
          reject(new Error('EnvConfig not ready'));
        }
      });
    },
    enabled: INTEGRATION_SERVICE_ENABLED === true,
    retry: 8,
    retryDelay: (attemptIndex) => Math.min(100 * Math.pow(2, attemptIndex), 1000),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return { 
    isMfeEnabled: INTEGRATION_SERVICE_ENABLED === true ? isMfeEnabled : false, 
    isLoading: INTEGRATION_SERVICE_ENABLED === true ? isLoading : false,
    isError: INTEGRATION_SERVICE_ENABLED === true ? isError : false,
    error: INTEGRATION_SERVICE_ENABLED === true ? error : null
  };
};

export default useIntegrationFeatureFlag;
